import { getToken, onMessage } from 'firebase/messaging';
import { queryClient } from '@/lib/api/queryClient';
import { queryKeys } from '@/lib/api/queryKeys';
import { notificationsApi } from '@/features/notifications/api';
import { FIREBASE_VAPID_KEY, getFcmMessaging } from '@/lib/firebase';
import { toast } from '@/components/ui/Toast';
import { translate } from '@/lib/i18n/translations';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';

let foregroundHandlerInstalled = false;

// Single-flight guard. React Strict Mode double-invokes effects in dev, so
// without this, two concurrent registerFcm() calls race and both POST the
// same token — the second one violates uq_user_device_token. We also cache
// the successful result so a later re-mount short-circuits entirely.
let registrationDone = false;
let registrationInFlight: Promise<void> | null = null;

/**
 * Reasons we couldn't register for push. The first two are actionable by the
 * user, so we surface them as a one-shot toast in addition to the console log;
 * the rest are dev / browser concerns and stay console-only.
 */
type FailReason =
  | 'no-window'
  | 'no-vapid'
  | 'unsupported'
  | 'insecure-context'
  | 'permission-denied'
  | 'sw-register-failed'
  | 'no-token'
  | 'register-device-failed'
  | 'unknown';

const NOTICE_KEY = 'fcm:notice-shown';

function notifyFailure(reason: FailReason, err?: unknown) {
   
  console.warn(`[fcm] registration ${reason}`, err ?? '');
  if (reason !== 'insecure-context' && reason !== 'permission-denied') return;
  if (typeof sessionStorage === 'undefined') return;
  if (sessionStorage.getItem(NOTICE_KEY) === '1') return;
  sessionStorage.setItem(NOTICE_KEY, '1');
  const language = usePrefsStore.getState().language;
  const bodyKey =
    reason === 'insecure-context'
      ? 'notifications.push.insecureContextBody'
      : 'notifications.push.permissionDeniedBody';
  toast({
    tone: 'info',
    title: translate(language, 'notifications.push.disabledTitle'),
    description: translate(language, bodyKey),
  });
}

/**
 * Request browser notification permission, fetch an FCM token, post it to the
 * backend, and install a foreground onMessage handler that refreshes the
 * unread-banner query.
 *
 * Idempotent across mounts and concurrent calls — see the single-flight
 * guards above. Failures are reported via console for every reason, plus a
 * one-shot info toast for the two cases the user can actually fix:
 * insecure-context (the dev URL isn't HTTPS or `localhost`, so the browser
 * blocks the service worker) and permission-denied.
 */
export function registerFcm(): Promise<void> {
  if (registrationDone) return Promise.resolve();
  if (registrationInFlight) return registrationInFlight;
  registrationInFlight = doRegister().finally(() => {
    registrationInFlight = null;
  });
  return registrationInFlight;
}

async function doRegister(): Promise<void> {
  try {
    if (typeof window === 'undefined' || typeof Notification === 'undefined') {
      notifyFailure('no-window');
      return;
    }
    if (!FIREBASE_VAPID_KEY) {
      notifyFailure('no-vapid');
      return;
    }

    // Browsers expose `serviceWorker` and Push only in a secure context
    // (HTTPS or `localhost`). Custom hostnames mapped in /etc/hosts are
    // NOT secure even when they resolve to 127.0.0.1 — Chrome checks
    // the hostname literal. Surface this case loudly so devs don't lose
    // an hour wondering why their FCM token never appears in the DB.
    if (!window.isSecureContext || !('serviceWorker' in navigator)) {
      notifyFailure('insecure-context');
      return;
    }

    const messaging = await getFcmMessaging();
    if (!messaging) {
      notifyFailure('unsupported');
      return;
    }

    let permission = Notification.permission;
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }
    if (permission !== 'granted') {
      notifyFailure('permission-denied');
      return;
    }

    // Register the Firebase messaging service worker shipped at
    // /firebase-messaging-sw.js, then wait for it to be active before asking
    // for a token. PushManager.subscribe (which getToken calls under the
    // hood) throws AbortError when no SW is active yet, which is exactly
    // what we used to hit on the first registration after a fresh load.
    let swReg: ServiceWorkerRegistration | undefined;
    try {
      swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      await navigator.serviceWorker.ready;
    } catch (err) {
      notifyFailure('sw-register-failed', err);
      // Foreground push still works without the SW — keep going.
    }

    const token = await getToken(messaging, {
      vapidKey: FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
    if (!token) {
      notifyFailure('no-token');
      return;
    }

    try {
      await notificationsApi.registerDevice({ token, platform: 'web' });
    } catch (err) {
      notifyFailure('register-device-failed', err);
      return;
    }

    registrationDone = true;
     
    console.info('[fcm] device registered for push');

    if (!foregroundHandlerInstalled) {
      foregroundHandlerInstalled = true;
      onMessage(messaging, () => {
        // A push landed while the tab is in the foreground — invalidate so the
        // banner picks it up. The notification itself comes via the realtime
        // socket or the next inbox refetch.
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unread });
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications.inbox });
      });
    }
  } catch (err) {
    notifyFailure('unknown', err);
  }
}
